import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.

/* eslint-disable react/no-deprecated */
import React, { useState, useEffect } from 'react';
import ContentLoader from '../ContentLoader';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import { loadImage } from './helpers';
import noImage from './images/no-image.svg';

var determineSource = function determineSource(_ref) {
  var image = _ref.image,
      background = _ref.background;

  if (image) {
    return {
      url: image,
      type: 'image'
    };
  } else if (background) {
    return {
      url: background,
      type: 'background'
    };
  }

  return {
    url: noImage,
    type: 'noImage'
  };
};
/**
 * `PreviewPanel` component is used for displaying website preview.
 * @since 0.3.0
 */


var PreviewPanel = function PreviewPanel(_ref2) {
  var background = _ref2.background,
      image = _ref2.image,
      _ref2$padding = _ref2.padding,
      padding = _ref2$padding === void 0 ? 0 : _ref2$padding,
      alt = _ref2.alt,
      children = _ref2.children,
      className = _ref2.className,
      _ref2$baseClassName = _ref2.baseClassName,
      baseClassName = _ref2$baseClassName === void 0 ? "".concat(CLS_PREFIX, "preview-panel") : _ref2$baseClassName,
      html = _ref2.html,
      props = _objectWithoutProperties(_ref2, ["background", "image", "padding", "alt", "children", "className", "baseClassName", "html"]);

  var source = determineSource({
    image: image,
    background: background
  });

  var _useState = useState(source.type === 'noImage' ? 'success' : 'loading'),
      _useState2 = _slicedToArray(_useState, 2),
      status = _useState2[0],
      setStatus = _useState2[1];

  useEffect(function () {
    var applyStatus = setStatus;

    var cleanup = function cleanup() {
      applyStatus = function applyStatus() {};
    };

    if (source.type === 'noImage') {
      applyStatus('success');
      return cleanup;
    }

    setStatus('loading');
    loadImage(source.url, {
      onSuccess: function onSuccess() {
        return applyStatus('success');
      },
      onFailure: function onFailure() {
        return applyStatus('failure');
      }
    });
    return cleanup;
  }, [source.type, source.url]);

  var renderLoader = function renderLoader() {
    if (status === 'loading') {
      return React.createElement("div", {
        className: "".concat(baseClassName, "__loader")
      }, React.createElement(ContentLoader, null));
    }

    return null;
  };

  var renderBackground = function renderBackground() {
    if (source.type !== 'background') {
      return null;
    }

    var src;

    switch (status) {
      case 'success':
        src = source.url;
        break;

      case 'failure':
        src = noImage;
        break;

      default:
        return null;
    }

    return {
      style: _objectSpread({}, props.style, {
        backgroundImage: "url(\"".concat(src, "\")")
      })
    };
  };

  var renderImage = function renderImage() {
    if (source.type !== 'image' && source.type !== 'noImage') {
      return null;
    }

    var src;

    switch (status) {
      case 'success':
        src = source.url;
        break;

      case 'failure':
        src = noImage;
        break;
    }

    var imageProps = {};

    if (status !== 'loading') {
      imageProps.src = src;

      if (alt) {
        imageProps.alt = alt;
      }
    }

    if (padding) {
      imageProps.style = {
        padding: "".concat(padding instanceof Array ? padding.join('px ') : padding, "px")
      };
    }

    return React.createElement("img", _extends({
      className: "".concat(baseClassName, "__image")
    }, imageProps));
  };

  return React.createElement("div", _extends({
    className: classNames(baseClassName, _defineProperty({}, "".concat(baseClassName, "--background"), source.type === 'background'), "".concat(baseClassName, "--").concat(status), className)
  }, props, renderBackground()), renderLoader(), renderImage(), React.createElement("div", {
    className: classNames("".concat(baseClassName, "__content"), _defineProperty({}, "".concat(baseClassName, "__content--empty"), !children))
  }, React.createElement("div", {
    className: "".concat(baseClassName, "__content-inner")
  }, children)));
};

export default PreviewPanel;